import { MainKanban } from "@/components/crm/kanban";
import {
  Avatar,
  Button,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  getGetBoardQueryKey,
  getGetLaneInfiniteQueryKey,
  GetLaneInvoiceState,
  GetLaneParams,
  useGetBoard,
  useGetCurrentOrganization,
  useGetEmails,
  useGetWorkflows,
} from "@/models/backend";
import { createFileRoute } from "@tanstack/react-router";
import { z, ZodObject, ZodType } from "zod";
import { Settings } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { CrmConfigure } from "@/components/crm/configure";
import { useThrottleCallback } from "@react-hook/throttle";
import { useAppForm } from "@/helpers/app-form";
import { useStore } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { $strip } from "zod/v4/core";
import { AssigneeMenuItem } from "@/components/crm/assignee";

function withZodSchema<Model>() {
  return <Schema extends { [K in keyof Model]: ZodType<Model[K]> }>(
    schema: Schema,
  ): ZodObject<Schema, $strip> => {
    return z.object(schema);
  };
}

export type BoardParams = Pick<
  GetLaneParams,
  "invoice_state" | "workflow_id" | "search" | "assignee"
>;

const filterSchema = withZodSchema<BoardParams>()({
  invoice_state: z.enum(GetLaneInvoiceState).catch("overdue"),
  workflow_id: z.string().nullish(),
  search: z.string().nullish(),
  assignee: z.string().nullish(),
});

export const Route = createFileRoute("/_authenticated/kanban")({
  component: RouteComponent,
  validateSearch: filterSchema.partial(),
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const { workflow_id, invoice_state, search, assignee } = searchParams;
  const { data: organization } = useGetCurrentOrganization();
  const { data: emails } = useGetEmails({ include_users: true });
  const navigate = Route.useNavigate();
  const displayFilter = localStorage.getItem(`kanban-display-filter-${organization?.id}`);
  const getDefault = () => {
    if (
      invoice_state != undefined ||
      workflow_id != undefined ||
      search != undefined ||
      assignee != undefined
    ) {
      return filterSchema.parse({ workflow_id, invoice_state, search, assignee });
    }
    if (displayFilter != null) {
      const parsed = filterSchema.safeParse(JSON.parse(displayFilter));
      if (parsed.success) {
        return parsed.data;
      }
    }
    return filterSchema.parse({});
  };
  const form = useAppForm({
    defaultValues: getDefault(),
  });
  const { data: workflows } = useGetWorkflows();
  const [configuring, setConfiguring] = useState(false);

  const formValues = useStore(form.store, (s) => s.values);
  const queryClient = useQueryClient();
  const debouncedNavigate = useThrottleCallback(
    () => {
      localStorage.setItem(`kanban-display-filter-${organization?.id}`, JSON.stringify(formValues));
      queryClient.invalidateQueries({ queryKey: getGetBoardQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetLaneInfiniteQueryKey() });
      navigate({
        search: {
          ...searchParams,
          ...Object.fromEntries(Object.entries(formValues).filter(([_k, v]) => v)),
        },
      });
    },
    4,
    false,
  );
  useEffect(() => {
    debouncedNavigate();
  }, [
    formValues.assignee,
    formValues.invoice_state,
    formValues.search,
    formValues.workflow_id,
    debouncedNavigate,
  ]);

  const { data: boardData, isLoading } = useGetBoard({
    query: { staleTime: Infinity },
  });

  return (
    <form.AppForm>
      <Stack spacing={2} sx={{ mt: 3, flex: 1 }}>
        <Stack spacing={2} direction="row" sx={{ mx: 3 }}>
          <Typography variant="h3">Board</Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => setConfiguring(true)}
            sx={{ ml: "auto" }}
            startIcon={<Settings />}
          >
            Configure
          </Button>
        </Stack>
        <Stack spacing={2} direction="row" sx={{ mx: 3 }}>
          <form.AppField name="invoice_state">
            {(field) => (
              <field.TextField
                size="small"
                select
                label="Included customers"
                sx={{ minWidth: "200px" }}
              >
                <MenuItem value="overdue">All overdue</MenuItem>
                <MenuItem value="outstanding">All outstanding</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </field.TextField>
            )}
          </form.AppField>
          <form.AppField name="workflow_id">
            {(field) => (
              <field.TextField
                size="small"
                label="Workflow"
                sx={{ minWidth: "200px" }}
                select
                slotProps={{
                  select: {
                    displayEmpty: true,
                  },
                }}
                value={field.state.value == undefined ? "" : field.state.value}
                onChange={(e) => field.handleChange(e.target.value == "" ? null : e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {workflows?.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
                <MenuItem value="excluded">Excluded / No workflow</MenuItem>
              </field.TextField>
            )}
          </form.AppField>
          <form.AppField name="assignee">
            {(field) => (
              <field.TextField
                size="small"
                label="Assignee"
                sx={{ minWidth: "200px" }}
                select
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (v) => {
                      if (v == "") {
                        return "All";
                      }
                      if (v == "unassigned") {
                        return "Unassigned";
                      }
                      const email = emails?.find((e) => e.id == v);
                      return email
                        ? [email.first_name, email.last_name].filter(Boolean).join(" ") ||
                            email.email
                        : null;
                    },
                  },
                }}
                value={field.state.value == null ? "" : field.state.value}
                onChange={(e) => {
                  field.handleChange(e.target.value == "" ? null : e.target.value);
                }}
              >
                <MenuItem value="">
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }} />
                  </ListItemAvatar>
                  <ListItemText primary="All" />
                </MenuItem>
                {emails?.map((e) => (
                  <AssigneeMenuItem email={e} key={e.id} value={e.id} />
                ))}
                <MenuItem value="unassigned">
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>??</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary="Unassigned" />
                </MenuItem>
              </field.TextField>
            )}
          </form.AppField>
          <form.AppField name="search">
            {(field) => <field.TextField size="small" label="Filter" />}
          </form.AppField>
        </Stack>
        {isLoading || !boardData ? (
          <Stack direction="row" spacing={2} sx={{ ml: 3, height: "100%" }}>
            <Skeleton
              variant="rectangular"
              height="100%"
              width="260px"
              sx={{
                borderRadius: 2,
                p: 1.5,
              }}
            />
            <Skeleton
              variant="rectangular"
              height="100%"
              width="260px"
              sx={{
                borderRadius: 2,
                p: 1.5,
              }}
            />
            <Skeleton
              variant="rectangular"
              height="100%"
              width="260px"
              sx={{
                borderRadius: 2,
                p: 1.5,
              }}
            />
          </Stack>
        ) : (
          <MainKanban lanes={boardData?.lanes ?? []} filter={formValues} />
        )}
        {configuring && <CrmConfigure open={configuring} onClose={() => setConfiguring(false)} />}
      </Stack>
    </form.AppForm>
  );
}
